import { useMemo, useState } from 'react';

import { SearchableVendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';
import P from '@zola/zola-ui/src/typography/Paragraphs';

import cx from 'classnames';

import { loadQuizzesAnswered } from '~/actions/couples/quizActions';
import { fetchFirstMovePreferences } from '~/components/common/modals/InvitesFromVendorsOptIn/invitesFromVendors';
import { getProviderWord } from '~/components/common/modals/MiniQuizModal/getProviderWord';
import { useMiniQuizModal } from '~/components/common/modals/MiniQuizModal/showMiniQuizModal';
import { useUserContext } from '~/contexts/UserContext';
import { useAppSelector } from '~/reducers';
import { useAppDispatch } from '~/reducers';
import { getQuizzesAnswered } from '~/reducers/couples/quizReducer';

import { MiniQuizMailboxIcon } from '../../MiniQuiz/MiniQuizMailboxIcon/MiniQuizMailboxIcon';

import styles from './miniQuizCard.module.less';

export const MiniQuizCard = ({
  taxonomyKey,
  className,
  onQuizSubmitted,
  noBorder = false,
}: {
  taxonomyKey: SearchableVendorTaxonomyKey;
  className?: string;
  onQuizSubmitted: () => void;
  noBorder?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { showMiniQuizModal } = useMiniQuizModal();
  const quizzesAnswered = useAppSelector(getQuizzesAnswered);
  const vendorWord = getProviderWord(taxonomyKey);
  const [loaded, setLoaded] = useState(false);
  const [optedIntoFirstMoves, setOptedIntoFirstMoves] = useState(false);
  const userContext = useUserContext();

  useEffectOnce(() => {
    if (userContext?.is_guest) {
      setLoaded(true);
    } else {
      Promise.all([
        fetchFirstMovePreferences([
          'FIRST_MOVES_SHARE_FAVORITES',
          'FIRST_MOVES_SHARE_INQUIRIES',
          'FIRST_MOVES_SHARE_WEDDING_DETAILS',
        ]),
        dispatch(loadQuizzesAnswered()),
      ])
        .then(([preferences]) => {
          setOptedIntoFirstMoves(
            Boolean(preferences.FIRST_MOVES_SHARE_FAVORITES?.enabled) ||
              Boolean(preferences.FIRST_MOVES_SHARE_INQUIRIES?.enabled) ||
              Boolean(preferences.FIRST_MOVES_SHARE_WEDDING_DETAILS?.enabled)
          );
        })
        .catch(() => null)
        .finally(() => {
          setLoaded(true);
        });
    }
  });

  const hedCopy = useMemo(() => {
    if (optedIntoFirstMoves && quizzesAnswered.length > 0) {
      return 'Let more vendors say hello!';
    }
    return 'Let vendors say hello first!';
  }, [optedIntoFirstMoves, quizzesAnswered.length]);

  const dekCopy = useMemo(() => {
    if (optedIntoFirstMoves && quizzesAnswered.length > 0) {
      return `Just confirm your details, and ${vendorWord} can say hello to you.`;
    }
    if (optedIntoFirstMoves && quizzesAnswered.length === 0) {
      return 'Just tell us a few details, and more vendors can say hello to you.';
    }
    return "They'll see your details and message you on Zola if it's a fit.";
  }, [optedIntoFirstMoves, quizzesAnswered.length, vendorWord]);

  return loaded ? (
    <div
      className={cx(styles.miniQuizSearchResultCard, className, { [styles.noBorder]: noBorder })}
    >
      <MiniQuizMailboxIcon />
      <div className={styles.hed}>{hedCopy}</div>
      <P.BodyBase className={styles.dek}>{dekCopy}</P.BodyBase>
      <ButtonV3
        onClick={() => {
          showMiniQuizModal({
            taxonomyKey,
            onQuizSubmitted: onQuizSubmitted,
            ctaPlacement: 'POST_ACTION_PROMPT',
            flowEntry: 'LISTING_PAGE_TILE',
            businessUnit: 'MARKETPLACE',
          });
        }}
      >
        Let&apos;s do this!
      </ButtonV3>
    </div>
  ) : (
    <></>
  );
};
